import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import {
  Check, Crown, Zap, ArrowRight, Fingerprint, MapPin, ClipboardList,
  ShieldCheck, Brain, Upload, Sun, Moon, Building2, X, Users, HardHat, Gift,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, LOGO } from "../lib/api";
import { formatCurrency } from "../i18n/format";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { InstallButton } from "../components/InstallButton";
import { ContractFlow } from "../components/ContractFlow";
import { AiHelp } from "../components/AiHelp";
import { ProgereLegalFooter } from "../components/ProgereLegalFooter";

const FEATURES = [
  { icon: Fingerprint, tk: "punchT", dk: "punchD" },
  { icon: MapPin, tk: "gpsT", dk: "gpsD" },
  { icon: ClipboardList, tk: "sheetsT", dk: "sheetsD" },
  { icon: Brain, tk: "aiT", dk: "aiD" },
  { icon: Upload, tk: "exportT", dk: "exportD" },
  { icon: ShieldCheck, tk: "secT", dk: "secD" },
];

export default function Home() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [leadPlan, setLeadPlan] = useState(null);
  const [signupPlan, setSignupPlan] = useState(null);
  const nav = useNavigate();

  useEffect(() => { api.plans().then(setPlans).catch(() => {}); }, []);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("forfaits")) {
      setTimeout(() => document.getElementById("forfaits")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);
  if (user) return <Navigate to="/tableau" replace />;

  return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(circle at 50% -10%, rgba(var(--gold-rgb),0.12), transparent 50%), var(--bg)" }}>
      {/* Nav */}
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 6vw", position: "sticky", top: 0, zIndex: 20, background: "color-mix(in srgb, var(--bg) 80%, transparent)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--border-gold)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={LOGO} alt="" referrerPolicy="no-referrer" style={{ width: 42, height: 42, objectFit: "contain" }} onError={(e) => e.currentTarget.style.display = "none"} />
          <span className="wordmark-lg" style={{ fontSize: 20 }}><span className="pro">PRO</span><span className="gere">GÈRE</span> <span className="punch">PUNCH</span></span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button className="top-btn" onClick={toggle} data-testid="home-theme">{theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</button>
          <button className="btn btn-gold" onClick={() => nav("/login")} data-testid="home-login-btn">{t("home.login")} <ArrowRight size={15} /></button>
        </div>
      </header>

      {/* Hero */}
      <section style={{ textAlign: "center", padding: "70px 6vw 50px", maxWidth: 880, margin: "0 auto" }} className="fade-in">
        <h1 className="wordmark-lg" style={{ fontSize: "min(56px, 11vw)", marginBottom: 14 }}>
          <span className="pro">PRO</span><span className="gere">GÈRE</span> <span className="punch">PUNCH</span>
        </h1>
        <p style={{ fontSize: 19, color: "var(--text)", fontWeight: 600, marginBottom: 8 }}>{t("home.heroLead")}</p>
        <p style={{ fontSize: 15, color: "var(--muted)", maxWidth: 620, margin: "0 auto 28px", lineHeight: 1.6 }}>
          {t("home.heroSub")}
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button className="btn btn-gold btn-lg" onClick={() => nav("/login")} data-testid="home-cta">{t("home.ctaStart")}</button>
          <a href="#forfaits" className="btn btn-outline btn-lg">{t("home.viewPlans")}</a>
          <InstallButton className="btn btn-outline btn-lg" label={t("home.install")} size={18} />
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: "10px 6vw 50px", maxWidth: 1100, margin: "0 auto" }}>
        <div className="grid-modules stagger">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.tk} className="card hoverable">
                <div className="mc-ico"><Icon size={22} /></div>
                <h4 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700 }}>{t(`home.feat.${f.tk}`)}</h4>
                <p style={{ color: "var(--muted)", fontSize: 13.5, margin: 0, lineHeight: 1.55 }}>{t(`home.feat.${f.dk}`)}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <section id="forfaits" style={{ padding: "30px 6vw 70px", maxWidth: 1200, margin: "0 auto" }}>
        <h2 className="page-title" style={{ textAlign: "center", marginBottom: 6 }}>{t("home.plansTitle")} <span className="gold">{t("home.plansTitleGold")}</span> {t("home.plansTitleEnd")}</h2>
        <p className="page-sub" style={{ textAlign: "center", margin: "0 auto 34px" }}>{t("home.plansSub")}</p>
        <div className="grid-stats stagger">
          {plans.map((p) => {
            const eco = p.ecosystem;
            return (
              <div key={p.id} className={`card plan-card ${eco ? "eco" : ""}`} data-testid={`home-plan-${p.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  {eco ? <Crown size={20} color="var(--gold)" /> : <Zap size={20} color="var(--gold)" />}
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{p.name}</h4>
                </div>
                <div className="plan-price">
                  {p.on_request ? <span style={{ fontSize: 22 }}>{t("home.onRequest")}</span> : <>{formatCurrency(p.price)} <small>{t("home.perMonth")}</small></>}
                </div>
                <div className="plan-capacity" data-testid={`home-plan-capacity-${p.id}`}>
                  <span><Users size={13} /> {p.max_users >= 100000 ? t("home.unlimited") : p.max_users} {t("home.users")}</span>
                  <span><HardHat size={13} /> {p.max_employees >= 100000 ? t("home.unlimited") : `${t("home.upTo")} ${p.max_employees}`} {t("home.employees")}</span>
                </div>
                {p.ecosystem_modules && (
                  <div className="eco-modules" data-testid={`home-eco-modules-${p.id}`}>
                    <div className="eco-modules-title"><Crown size={12} /> {t("home.suiteIncluded")}</div>
                    <div className="eco-chips">{p.ecosystem_modules.map((m) => <span key={m} className="eco-chip">{m}</span>)}</div>
                  </div>
                )}
                <div style={{ margin: "14px 0", flex: 1 }}>
                  {p.features.slice(0, 6).map((f) => (
                    <div key={f} className="plan-feat"><Check size={15} color="var(--green)" style={{ flexShrink: 0, marginTop: 1 }} /> <span>{f}</span></div>
                  ))}
                </div>
                {p.on_request ? (
                  <button className="btn btn-gold" style={{ width: "100%" }} onClick={() => setLeadPlan(p)} data-testid={`home-request-${p.id}`}>{t("home.requestQuote")}</button>
                ) : (
                  <button className={`btn ${eco ? "btn-gold" : "btn-outline"}`} style={{ width: "100%" }} onClick={() => setSignupPlan(p)} data-testid={`home-choose-${p.id}`}>{t("home.choosePlan")}</button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer légal universel */}
      <ProgereLegalFooter />
      {leadPlan && <LeadModal plan={leadPlan} onClose={() => setLeadPlan(null)} />}
      {signupPlan && <SignupModal plan={signupPlan} onClose={() => setSignupPlan(null)} />}
      <AiHelp />
    </div>
  );
}

function SignupModal({ plan, onClose }) {
  const [phase, setPhase] = useState("account");
  const [form, setForm] = useState({ company_name: "", admin_name: "", admin_email: "", admin_password: "" });
  const [busy, setBusy] = useState(false);

  const finalize = async (signature) => {
    setBusy(true);
    try {
      const r = await api.signupCheckout({ ...form, plan_id: plan.id, origin_url: window.location.origin, signature });
      window.location.href = r.url;
    } catch (err) {
      toast.error("Impossible de démarrer le paiement", { description: err?.response?.data?.detail || "Réessayez." });
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="signup-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: phase === "account" ? 460 : 660 }}>
        <button className="modal-close" onClick={onClose} data-testid="signup-modal-close"><X size={18} /></button>
        <h3 style={{ margin: "0 0 4px", fontSize: 19 }}>{plan.name}</h3>
        <div className="plan-price" style={{ fontSize: 24, margin: "2px 0 8px" }}>{plan.price}$ <small>CAD/mois</small></div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(34,197,94,.13)", border: "1px solid rgba(34,197,94,.35)", color: "#22c55e", padding: "5px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 700, marginBottom: 14 }} data-testid="trial-badge">
          <Gift size={14} /> 14 jours d'essai gratuit — sans engagement
        </div>

        {phase === "account" ? (
          <form onSubmit={(e) => { e.preventDefault(); setPhase("contract"); }} data-testid="signup-account-step">
            <p style={{ color: "var(--muted)", fontSize: 13, margin: "0 0 16px", lineHeight: 1.5 }}>
              Étape 1 — Créez votre compte. Vous signerez ensuite le contrat de service, puis démarrerez votre essai gratuit de 14 jours (aucun prélèvement avant la fin de l'essai).
            </p>
            <label className="field-label">Nom de l'entreprise *</label>
            <input className="input" style={{ width: "100%", marginBottom: 12 }} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required data-testid="signup-company" />
            <label className="field-label">Votre nom *</label>
            <input className="input" style={{ width: "100%", marginBottom: 12 }} value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} required data-testid="signup-name" />
            <label className="field-label">Courriel *</label>
            <input className="input" style={{ width: "100%", marginBottom: 12 }} type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required data-testid="signup-email" />
            <label className="field-label">Mot de passe *</label>
            <input className="input" style={{ width: "100%", marginBottom: 6 }} type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} required data-testid="signup-password" />
            <p style={{ color: "var(--muted)", fontSize: 11.5, margin: "0 0 16px" }}>8+ caractères, avec majuscule, minuscule et chiffre.</p>
            <button className="btn btn-gold" style={{ width: "100%" }} data-testid="signup-submit">Continuer <ArrowRight size={15} /></button>
          </form>
        ) : (
          <ContractFlow plan={plan} prefill={{ full_name: form.admin_name, company_name: form.company_name }}
            leadingDone={["Compte"]} submitLabel="Démarrer l'essai gratuit" busy={busy}
            onComplete={finalize} onBack={() => setPhase("account")} />
        )}
      </div>
    </div>
  );
}

function LeadModal({ plan, onClose }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", employees: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.createLead({ ...form, plan_id: plan.id });
      setSent(true);
    } catch (err) {
      toast.error("Échec de l'envoi", { description: err?.response?.data?.detail || "Réessayez plus tard." });
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="lead-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <button className="modal-close" onClick={onClose} data-testid="lead-modal-close"><X size={18} /></button>
        {sent ? (
          <div style={{ textAlign: "center", padding: "20px 6px" }} data-testid="lead-success">
            <div className="mc-ico" style={{ margin: "0 auto 14px" }}><Check size={24} color="var(--green)" /></div>
            <h3 style={{ margin: "0 0 8px", fontSize: 18 }}>Demande envoyée !</h3>
            <p style={{ color: "var(--muted)", fontSize: 14, lineHeight: 1.6 }}>
              Merci. Notre équipe vous contactera rapidement pour votre soumission <b>{plan.name}</b>.
            </p>
            <button className="btn btn-gold" style={{ marginTop: 16 }} onClick={onClose} data-testid="lead-done-btn">Fermer</button>
          </div>
        ) : (
          <>
            <h3 style={{ margin: "0 0 4px", fontSize: 19 }}><Crown size={18} color="var(--gold)" style={{ verticalAlign: "-3px" }} /> {plan.name}</h3>
            <p style={{ color: "var(--muted)", fontSize: 13.5, margin: "0 0 18px", lineHeight: 1.5 }}>
              Laissez-nous vos coordonnées et recevez une soumission personnalisée pour l'écosystème complet.
            </p>
            <form onSubmit={submit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div><label className="field-label">Nom complet *</label><input className="input" style={{ width: "100%" }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="lead-name" /></div>
                <div><label className="field-label">Entreprise *</label><input className="input" style={{ width: "100%" }} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} required data-testid="lead-company" /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                <div><label className="field-label">Courriel *</label><input className="input" style={{ width: "100%" }} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="lead-email" /></div>
                <div><label className="field-label">Téléphone</label><input className="input" style={{ width: "100%" }} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" /></div>
              </div>
              <label className="field-label" style={{ marginTop: 10, display: "block" }}>Nombre d'employés</label>
              <input className="input" style={{ width: "100%" }} value={form.employees} onChange={(e) => setForm({ ...form, employees: e.target.value })} placeholder="ex. 25" data-testid="lead-employees" />
              <label className="field-label" style={{ marginTop: 10, display: "block" }}>Message</label>
              <textarea className="input" style={{ width: "100%", minHeight: 70, resize: "vertical" }} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Vos besoins, modules souhaités…" data-testid="lead-message" />
              <button className="btn btn-gold" style={{ width: "100%", marginTop: 16 }} disabled={busy} data-testid="lead-submit">
                {busy ? "Envoi…" : "Envoyer la demande"} <ArrowRight size={15} />
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
